import { useMemo, type ReactNode } from "react";
import { Info } from "lucide-react";
import { useAuth } from "@ncp/api-client";
import { useLocale, useTranslation } from "@ncp/i18n";
import { ConfidenceScaleProvider, Tooltip, cn } from "@ncp/ui";
import { useCalculationMethod, type MeasureKey } from "./figures";

/**
 * The ⓘ beside a figure: what the measure is (from the published method), the method version and whether it is approved, and the values
 * this figure rests on – enough to reproduce it (condition 2.16).
 */
export function FigureInfo({ measure, inputs, className }: { measure: MeasureKey; inputs?: Array<ReactNode | null | false | undefined>; className?: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const method = useCalculationMethod();
  const ar = locale === "ar";
  const m = method.data?.measures.find((x) => x.key === measure);
  const name = m ? (ar ? m.nameAr : m.nameEn) : t(`backoffice:r3Metrics.measures.${measure}`);
  const shown = (inputs ?? []).filter(Boolean);
  return (
    <Tooltip
      content={
        <span className="block max-w-xs space-y-1 text-start text-xs leading-relaxed">
          <span className="block font-semibold">{name}</span>
          {m ? <span className="block">{ar ? m.definitionAr : m.definitionEn}</span> : null}
          {m ? <span className="block font-mono text-[11px] opacity-80" dir="ltr">{m.formula}</span> : null}
          {shown.length ? <span className="block">{t("backoffice:r3Metrics.inputs")}: {shown.map((x, i) => <span key={i}>{i > 0 ? (ar ? "، " : "; ") : ""}{x}</span>)}</span> : null}
          {method.data ? <span className="block opacity-70">{t("backoffice:r3Metrics.methodVersion", { version: method.data.version })} · {ar ? method.data.statusAr : method.data.statusEn}</span> : null}
        </span>
      }
    >
      <button type="button" aria-label={t("backoffice:r3Metrics.aboutFigure", { name })} className={cn("inline-grid size-5 shrink-0 place-items-center rounded-full text-sand-500 hover:text-aegold-700", className)}>
        <Info className="size-3.5" />
      </button>
    </Tooltip>
  );
}

/** A figure with its name and ⓘ, for key/value grids. */
export function FigureLabel({ children, measure, inputs }: { children: ReactNode; measure: MeasureKey; inputs?: Array<ReactNode | null | false | undefined> }) {
  return <span className="inline-flex items-center gap-1">{children}<FigureInfo measure={measure} inputs={inputs} /></span>;
}

/**
 * Condition 2.16: an agent draft's confidence, wherever the shared draft card shows it, is read on the bands of the published method and
 * carries the ⓘ of the measure – not the card's built-in bands and a hover text.
 */
export function MethodConfidenceScale({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const method = useCalculationMethod(isAuthenticated);
  const bands = method.data?.bands;
  const value = useMemo(() => (bands ? { highFrom: bands.confidenceHighFromPercent, mediumFrom: bands.confidenceMediumFromPercent, info: <FigureInfo measure="draftConfidence" /> } : null), [bands]);
  return <ConfidenceScaleProvider value={value}>{children}</ConfidenceScaleProvider>;
}
